import { Combobox, FormField, FormHelp } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { Fragment, useContext } from 'react';
import { Skeleton, SkeletonListItem } from './Skeleton';
import usePropertyFilter, { type UpdatePathWithListingAlias } from '../../hooks/usePropertyFilter';
import { IsVisible } from '@zg-rentals/react-hooks';
import { MultiFamilyLeadsPageContext } from '../../contexts/MultiFamilyLeadsContextProvider';
import { useRouter } from 'next/router';

export const formFieldStyles = css.raw({
  minWidth: '309px',
  '& > ul': {
    maxHeight: '268px',
  },
});

export default function PropertyDropdown() {
  const router = useRouter();
  const { listingAlias } = useContext(MultiFamilyLeadsPageContext);

  const updatePathWithListingAlias: UpdatePathWithListingAlias = (listingAlias) => {
    const updatedRouter = {
      pathname: router.pathname,
      query: {
        ...router.query,
        listingAlias,
      },
    };
    router.push(updatedRouter);
  };

  const resetPropertyFilter = () => {
    delete router.query.listingAlias;
    router.push({
      pathname: router.pathname,
      query: router.query,
    });
  };

  const {
    defaultListingAddress,
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    propertyListings,
    shouldShowSkeleton,
  } = usePropertyFilter({
    defaultListingAliasOnLoad: listingAlias,
    resetPropertyFilter,
    updatePathWithListingAlias,
  });

  const isLoadingPropertyAddress = listingAlias && shouldShowSkeleton;

  return (
    <FormField
      error={!!errorMessage}
      description={<FormHelp>{errorMessage}</FormHelp>}
      css={formFieldStyles}
      control={
        isLoadingPropertyAddress ? (
          <Skeleton />
        ) : (
          <Combobox
            appearance="select"
            fluidDropdown
            focusFirstOption
            freeForm
            onChange={handleChange}
            onOptionSelect={handleSelectProperty}
            options={propertyListings}
            placeholder="Enter a property"
            selectCallbackFrequency="change"
            shouldAwaitInteractionResponse
            showLabelForValue
            size="lg"
            value={defaultListingAddress || listingAlias}
            renderOption={({
              option,
              highlightPattern,
              key,
              ref,
              renderOptionLabel,
              renderOptionMeta,
              renderOptionMedia: _renderOptionMedia,
              ...rest
            }) => {
              if (typeof option !== 'object') return;

              const { value, label, meta } = option;
              const isLastItemInList = value === propertyListings[propertyListings.length - 1].value;

              return (
                <Fragment key={key}>
                  <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
                    {renderOptionLabel?.({ children: label, highlightPattern })}
                    {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
                  </Combobox.Option>

                  {isLastItemInList && hasMoreToPaginate && (
                    <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                      {({ ref }) => <SkeletonListItem ref={ref} />}
                    </IsVisible>
                  )}
                </Fragment>
              );
            }}
            css={{
              backgroundColor: 'bg.action.neutral.default',
            }}
          />
        )
      }
    />
  );
}
